<script setup>
// Types of trail: one card per kind of trail the organization has, and
// the networks it is found in. A type no public trail has is left off,
// so the block only ever describes what a visitor can actually ride.
import { computed, inject } from 'vue';
import './trailsPage.css';
import { blockStyle, described, trailTypesIn } from '../trailsPage.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const types = computed(() => trailTypesIn(props.site_data));
const accent = computed(() => props.config.style?.accent || props.accent || 'var(--th-accent, #2f6b47)');
const pad = (n) => String(n).padStart(2, '0');
const editing = inject('tiqEditorCanvas', false);
</script>

<template>
    <section v-if="types.length || editing" class="tp ttypes" :id="config.anchor_id || null" :style="[blockStyle(config), { '--tp-accent': accent }]">
        <div class="tp-wrap">
            <header v-if="config.title || config.subtitle" class="tp-head">
                <h2 v-if="config.title" class="tp-title">{{ config.title }}</h2>
                <p v-if="config.subtitle" class="tp-sub">{{ config.subtitle }}</p>
            </header>
            <p v-if="!(types.length)" class="tp-empty">Nothing to show yet. This block lists the types set on your trails: open a trail in the dashboard and pick its type under Trail type.</p>
            <div class="ttypes-grid">
                <article v-for="(ty, i) in types" :key="ty.key" class="ttypes-card">
                    <p class="tp-mono ttypes-num">Type {{ pad(i + 1) }}</p>
                    <h3 class="ttypes-name">{{ ty.label }}</h3>
                    <p class="ttypes-desc">{{ described(config, ty.key, ty.description) }}</p>
                    <div v-if="ty.networks.length" class="ttypes-where">
                        <p class="tp-mono ttypes-label">Find it at</p>
                        <div class="ttypes-tags">
                            <component :is="n.href ? 'a' : 'span'" v-for="n in ty.network_links" :key="n.name"
                                       :href="n.href || undefined" class="tp-tag" :class="{ 'ttypes-link': n.href }">{{ n.name }}</component>
                        </div>
                    </div>
                </article>
            </div>
        </div>
    </section>
</template>

<style scoped>
.ttypes-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 16px; }
.ttypes-card {
    display: flex; flex-direction: column; background: var(--tp-card);
    border: 1px solid var(--tp-line); border-radius: 4px; padding: 22px;
}
.ttypes-num { color: var(--tp-accent); margin: 0; }
.ttypes-name {
    font-family: var(--tp-display); font-weight: 800; font-size: 22px; line-height: 1.15;
    margin: 12px 0 0; color: var(--text-heading, inherit);
}
.ttypes-desc { color: var(--tp-muted); line-height: 1.5; margin: 10px 0 0; flex: 1; }
.ttypes-where { border-top: 1px solid var(--tp-line); margin-top: 16px; padding-top: 14px; }
.ttypes-label { color: var(--tp-faint); margin: 0 0 8px; }
.ttypes-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.ttypes-link { text-decoration: none; color: inherit; transition: border-color .12s, background .12s; }
.ttypes-link:hover { text-decoration: none; border-color: var(--tp-accent); }
</style>
